import React, { useState, useRef, useMemo } from 'react';
import { Paper } from 'design-system/Paper';

// This library uses pdfjs under the hood. It's not very nice as
// you can't select text in it. If further use case is needed one
// may investigate how to integrate pdfjs directly
// ref: https://github.com/mozilla/pdf.js
import { usePdf } from '@mikecousins/react-pdf';

/* eslint-disable restrict-imports/restrict-imports */
import Pagination from '@mui/material/Pagination';

interface PDFViewProps {
  src: string;
  renderWithPDFJs?: boolean;
  dontRenderDownload?: boolean;
}

const PDFView: React.FC<PDFViewProps> = ({ src, renderWithPDFJs, dontRenderDownload }) => {
  const renderFile = useMemo(() => {
    return (
      <div
        style={{
          height: '100%',
          paddingLeft: 24,
          paddingRight: 24,
        }}
      >
        <object
          type="application/pdf"
          data={src}
          width="100%"
          height="100%"
          style={{
            minHeight: 600,
          }}
        >
          <Paper>
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'blue',
              }}
            >
              Open PDF in New Tab
            </a>
          </Paper>
        </object>
      </div>
    );
  }, [src]);

  if (renderWithPDFJs) {
    // When the blob's contentType is not properly set to application/pdf,
    // we need to use PDFJS to render the document otherwise the default
    // behavior is to download the file.
    return <PDFJSView src={src} dontRenderDownload={dontRenderDownload} />;
  }

  return <div>{renderFile}</div>;
};

const PDFJSView: React.FC<{ src: string; dontRenderDownload?: boolean }> = ({
  src,
  dontRenderDownload,
}) => {
  const [page, setPage] = useState(1);
  const canvasRef = useRef(null);

  const { pdfDocument } = usePdf({
    file: src,
    page,
    canvasRef,
  });

  return (
    <div
      style={{
        height: '100%',
        paddingLeft: 24,
        paddingRight: 24,
      }}
    >
      {!dontRenderDownload && (
        <a href={src} target="_blank" rel="noopener noreferrer">
          Download PDF
        </a>
      )}

      {pdfDocument && pdfDocument.numPages ? (
        <div>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '10px 0px' }}>
            <Pagination
              count={pdfDocument.numPages}
              page={page}
              onChange={(_e: React.ChangeEvent<unknown>, page: number) => setPage(page)}
            />
          </div>
          <canvas ref={canvasRef} />
        </div>
      ) : (
        <div>Loading...</div>
      )}
    </div>
  );
};

export default PDFView;
